import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { storage } from "../../state/storage.js";
import { keyVault } from "../../platform/vault/index.js";
import { aiGateway } from "../../state/ai.js";
import { useT } from "../../i18n/use-t.js";
import type { TKey } from "../../i18n/strings.js";
import {
  createProviderProfile,
  getProviderCatalogEntry,
  PROVIDER_CATALOG,
  type ProviderSamplingDefaults,
  type ProviderCatalogEntry,
  type ProviderKind,
  type ProviderProfile,
  type ReasoningLevel,
  type Settings,
  type TokenizerMode,
  type TransportMode,
} from "@roqeet/core";

function kindLabel(kind: ProviderKind, t: (k: TKey) => string): string {
  switch (kind) {
    case "openai":
      return "OpenAI";
    case "anthropic":
      return "Anthropic (Claude)";
    case "google":
      return "Google (Gemini)";
    case "openrouter":
      return "OpenRouter";
    case "openai-compatible":
      return t("prov.kindLocalCustom");
    case "platform":
      return t("prov.kindPlatform");
    default:
      return kind;
  }
}

function profileLabel(profile: ProviderProfile, t: (k: TKey) => string): string {
  return getProviderCatalogEntry(profile.providerId)?.label ?? kindLabel(profile.kind, t);
}

function capabilityText(profile: ProviderProfile): string {
  const capabilities =
    profile.capabilities ?? getProviderCatalogEntry(profile.providerId)?.capabilities ?? ["chat"];
  return capabilities.join(", ");
}

export function ProvidersSection() {
  const { t } = useT();
  const [profiles, setProfiles] = useState<ProviderProfile[]>([]);
  const [settings, setSettings] = useState<Settings | undefined>();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<ProviderProfile | null>(null);

  const reload = async () => {
    const [list, s] = await Promise.all([storage.providerProfiles.list(), storage.getSettings()]);
    setProfiles(list);
    setSettings(s);
  };

  useEffect(() => {
    void reload();
  }, []);

  const setDefault = async (id: string) => {
    const current = await storage.getSettings();
    const next = { ...current, defaultProviderProfileId: id as Settings["defaultProviderProfileId"] } as Settings;
    await storage.putSettings(next);
    setSettings(next);
  };

  const remove = async (p: ProviderProfile) => {
    if (!confirm(t("prov.deleteConfirm", { name: p.name }))) return;
    if (p.keyRef) await keyVault.remove(p.keyRef).catch(() => {});
    await storage.providerProfiles.delete(p.id);
    await reload();
  };

  return (
    <section>
      <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-faint">
        {t("prov.title")}
      </h2>
      <div className="space-y-2">
        {profiles.length === 0 && !adding && (
          <p className="rounded-xl border border-line bg-bg-soft px-3 py-3 text-sm text-ink-faint">
            {t("prov.empty")}
          </p>
        )}

        <ul className="space-y-2">
          {profiles.map((p) => {
            const isDefault = settings?.defaultProviderProfileId === p.id;
            return (
              <li
                key={p.id}
                className="flex items-start justify-between gap-2 rounded-xl border border-line bg-bg-soft px-3 py-3 sm:items-center"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm text-ink">{p.name}</span>
                    {isDefault && (
                      <span className="rounded bg-accent/20 px-1.5 py-0.5 text-xs text-accent-soft">
                        {t("common.default")}
                      </span>
                    )}
                  </div>
                  <div className="truncate text-xs text-ink-faint">
                    {profileLabel(p, t)} · {p.defaultModel ?? t("prov.modelUnset")} ·{" "}
                    {p.keyRef ? t("prov.keySet") : t("prov.keyNone")}
                  </div>
                  <div className="truncate text-[11px] text-ink-faint">
                    {capabilityText(p)}
                  </div>
                </div>
                <div className="flex max-w-full shrink-0 flex-wrap items-center justify-end gap-1">
                  {!isDefault && (
                    <button
                      className="min-h-8 touch-manipulation rounded-lg border border-line px-2 py-1 text-xs text-ink-soft hover:bg-bg-raised"
                      onClick={() => setDefault(p.id)}
                    >
                      {t("common.makeDefault")}
                    </button>
                  )}
                  <button
                    className="min-h-8 touch-manipulation rounded-lg border border-line px-2 py-1 text-xs text-ink-soft hover:bg-bg-raised"
                    onClick={() => {
                      setAdding(false);
                      setEditing(p);
                    }}
                  >
                    {t("common.edit")}
                  </button>
                  <button
                    className="min-h-8 touch-manipulation rounded-lg border border-line px-2 py-1 text-xs text-red-300 hover:bg-bg-raised"
                    onClick={() => remove(p)}
                  >
                    {t("common.delete")}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>

        {adding ? (
          <ProviderProfileForm
            onDone={async () => {
              setAdding(false);
              await reload();
            }}
            onCancel={() => setAdding(false)}
            makeDefaultIfFirst={profiles.length === 0}
          />
        ) : editing ? (
          <ProviderProfileForm
            existing={editing}
            onDone={async () => {
              setEditing(null);
              await reload();
            }}
            onCancel={() => setEditing(null)}
            makeDefaultIfFirst={false}
          />
        ) : (
          <button
            className="w-full rounded-xl border border-dashed border-line px-3 py-3 text-sm text-ink-soft hover:bg-bg-raised"
            onClick={() => {
              setEditing(null);
              setAdding(true);
            }}
          >
            {t("prov.add")}
          </button>
        )}
      </div>
    </section>
  );
}

function ProviderProfileForm({
  existing,
  onDone,
  onCancel,
  makeDefaultIfFirst,
}: {
  existing?: ProviderProfile;
  onDone: () => void | Promise<void>;
  onCancel: () => void;
  makeDefaultIfFirst: boolean;
}) {
  const { t } = useT();
  const initial = initialCatalogFor(existing);
  const [catalogId, setCatalogId] = useState(existing?.providerId ?? initial.id);
  const [name, setName] = useState(existing?.name ?? initial.label);
  const [kind, setKind] = useState<ProviderKind>(existing?.kind ?? initial.kind);
  const [transport, setTransport] = useState<TransportMode>(existing?.transport ?? initial.transport);
  const [baseUrl, setBaseUrl] = useState(existing?.baseUrl ?? initial.baseUrl ?? "");
  const [proxyUrl, setProxyUrl] = useState(existing?.proxyUrl ?? "");
  const [model, setModel] = useState(existing?.defaultModel ?? initial.defaultModel ?? "");
  const [samplingDefaults, setSamplingDefaults] = useState<ProviderSamplingDefaults>(
    existing?.samplingDefaults ?? {},
  );
  const [apiKey, setApiKey] = useState("");
  const [models, setModels] = useState<string[]>(existing?.knownModels ?? initial.knownModels ?? []);
  const [fetching, setFetching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const tRef = useRef(t);
  const fetchSeq = useRef(0);
  const lastAutoFetchKey = useRef("");
  const catalog = getProviderCatalogEntry(catalogId) ?? initial;
  const canFetchModels = catalog.capabilities.includes("chat");
  const showBaseUrl =
    !!catalog.baseUrl ||
    catalog.baseUrlRequired ||
    kind === "openai-compatible";

  useEffect(() => {
    tRef.current = t;
  }, [t]);

  const applyCatalog = (entry: ProviderCatalogEntry) => {
    setCatalogId(entry.id);
    setName(entry.label);
    setKind(entry.kind);
    setTransport(entry.transport);
    setBaseUrl(entry.baseUrl ?? "");
    setProxyUrl("");
    setModel(entry.defaultModel ?? "");
    setSamplingDefaults({});
    setModels(entry.knownModels ?? []);
    if (entry.requiresApiKey === false) setApiKey("");
    setErr(null);
  };

  const knownModelsFor = (entry: ProviderCatalogEntry): string[] =>
    mergeModelLists([model.trim()], models, entry.knownModels ?? []);

  const providerInit = (entry: ProviderCatalogEntry, keyRef?: string) => ({
    name: name.trim() || entry.label,
    kind: entry.kind,
    transport,
    providerId: entry.id,
    capabilities: entry.capabilities,
    ...(baseUrl.trim() ? { baseUrl: baseUrl.trim() } : {}),
    ...(proxyUrl.trim() ? { proxyUrl: proxyUrl.trim() } : {}),
    ...(model.trim() ? { defaultModel: model.trim() } : {}),
    ...(knownModelsFor(entry).length ? { knownModels: knownModelsFor(entry) } : {}),
    ...(hasSamplingDefaults(samplingDefaults) ? { samplingDefaults } : {}),
    ...(entry.extraHeaders ? { extraHeaders: entry.extraHeaders } : {}),
    ...(keyRef ? { keyRef } : {}),
    extensions: {
      apiFamily: entry.apiFamily,
      ...(entry.imageProvider ? { imageProvider: entry.imageProvider } : {}),
      ...(entry.docsUrl ? { docsUrl: entry.docsUrl } : {}),
    },
  });

  const modelListProbe = useCallback((entry: ProviderCatalogEntry): ProviderProfile => {
    const init = {
      name: name.trim() || entry.label,
      kind: entry.kind,
      transport,
      providerId: entry.id,
      capabilities: entry.capabilities,
      ...(baseUrl.trim() ? { baseUrl: baseUrl.trim() } : {}),
      ...(proxyUrl.trim() ? { proxyUrl: proxyUrl.trim() } : {}),
      ...(entry.extraHeaders ? { extraHeaders: entry.extraHeaders } : {}),
      ...(existing?.keyRef ? { keyRef: existing.keyRef } : {}),
      extensions: {
        apiFamily: entry.apiFamily,
        ...(entry.imageProvider ? { imageProvider: entry.imageProvider } : {}),
        ...(entry.docsUrl ? { docsUrl: entry.docsUrl } : {}),
      },
    };

    if (existing) {
      return {
        ...existing,
        ...init,
        id: existing.id,
        sync: existing.sync,
        enabled: existing.enabled,
      };
    }
    return createProviderProfile(init);
  }, [baseUrl, existing, name, proxyUrl, transport]);

  const typedKey = apiKey.trim();
  const canUseStoredKey = !!existing?.keyRef && !typedKey;
  const canUseTypedKey = typedKey.length >= 8;
  const canListWithoutKey = catalog.requiresApiKey === false || catalog.id === "openrouter";
  const autoFetchKey =
    canFetchModels && (canUseStoredKey || canUseTypedKey || canListWithoutKey)
      ? [
          catalog.id,
          kind,
          transport,
          baseUrl.trim(),
          proxyUrl.trim(),
          existing?.keyRef ?? "",
          typedKey,
          canListWithoutKey ? "public" : "",
        ].join("\u001f")
      : "";

  const fetchModels = useCallback(async (opts: { silent?: boolean } = {}) => {
    if (!opts.silent) setErr(null);
    if (!canFetchModels) {
      if (!opts.silent) setErr(tRef.current("prov.fetchUnsupported"));
      return;
    }
    if (autoFetchKey) lastAutoFetchKey.current = autoFetchKey;
    const seq = ++fetchSeq.current;
    setFetching(true);
    try {
      const probe = modelListProbe(catalog);
      const list = typedKey
        ? await aiGateway.listModelsWithKey(probe, typedKey)
        : await aiGateway.listModels(probe);
      if (seq !== fetchSeq.current) return;
      setModels((current) => mergeModelLists(list, current, catalog.knownModels ?? [], [model.trim()]));
      if (list.length === 0 && !opts.silent) setErr(tRef.current("prov.fetchFailed"));
    } catch (e) {
      if (seq === fetchSeq.current) {
        setModels((current) => mergeModelLists(current, catalog.knownModels ?? [], [model.trim()]));
        if (!opts.silent) setErr(e instanceof Error ? e.message : String(e));
      }
    } finally {
      if (seq === fetchSeq.current) setFetching(false);
    }
  }, [autoFetchKey, canFetchModels, catalog, model, modelListProbe, typedKey]);

  useEffect(() => {
    if (!autoFetchKey) return;
    if (lastAutoFetchKey.current === autoFetchKey) return;

    const handle = window.setTimeout(() => {
      if (lastAutoFetchKey.current === autoFetchKey) return;
      lastAutoFetchKey.current = autoFetchKey;
      void fetchModels({ silent: true });
    }, canUseTypedKey ? 700 : 0);
    return () => window.clearTimeout(handle);
  }, [autoFetchKey, canUseTypedKey, fetchModels]);

  const setSamplingDefault = <K extends keyof ProviderSamplingDefaults>(
    key: K,
    value: ProviderSamplingDefaults[K],
  ) => {
    setSamplingDefaults((current) => {
      const next = { ...current, [key]: value };
      if (value === undefined || value === "auto") delete next[key];
      return next;
    });
  };

  const modelChoices = mergeModelLists(models, catalog.knownModels ?? [], [model.trim()]);

  const save = async () => {
    setErr(null);
    setSaving(true);
    try {
      if (catalog.baseUrlRequired && !baseUrl.trim()) {
        throw new Error(t("prov.baseUrlRequired", { name: catalog.label }));
      }
      let keyRef = existing?.keyRef;
      let oldKeyShouldBeRemoved = false;
      if (catalog.requiresApiKey === false) {
        oldKeyShouldBeRemoved = !!existing?.keyRef;
        keyRef = undefined;
      }
      if (apiKey.trim()) {
        keyRef = await keyVault.store(apiKey.trim());
        oldKeyShouldBeRemoved = !!existing?.keyRef && existing.keyRef !== keyRef;
      }
      let savedProfileId: ProviderProfile["id"] | undefined = existing?.id;
      if (existing) {
        const { keyRef: _oldKeyRef, ...withoutKey } = existing;
        void _oldKeyRef;
        const next: ProviderProfile = {
          ...withoutKey,
          ...providerInit(catalog, keyRef),
          id: existing.id,
          sync: existing.sync,
          enabled: existing.enabled,
        };
        if (keyRef) next.keyRef = keyRef;
        if (oldKeyShouldBeRemoved && existing.keyRef) {
          await keyVault.remove(existing.keyRef).catch(() => {});
        }
        await storage.providerProfiles.put(next);
      } else {
        const profile = createProviderProfile(providerInit(catalog, keyRef));
        await storage.providerProfiles.put(profile);
        savedProfileId = profile.id;
      }

      if (!existing && makeDefaultIfFirst && savedProfileId) {
        const s = await storage.getSettings();
        if (s) {
          await storage.putSettings({
            ...s,
            defaultProviderProfileId: savedProfileId,
          });
        }
      }
      await onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2 rounded-xl border border-line bg-bg-soft p-3">
      <Field label={t("prov.fieldName")}>
        <input
          className="w-full rounded-lg border border-line bg-bg-raised px-2 py-1.5 text-sm text-ink outline-none focus:border-accent"
          placeholder={t("prov.fieldNamePlaceholder")}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </Field>
      <Field label={t("prov.fieldKind")}>
        <select
          className="w-full rounded-lg border border-line bg-bg-raised px-2 py-1.5 text-sm text-ink"
          value={catalogId}
          onChange={(e) => {
            const entry = getProviderCatalogEntry(e.target.value);
            if (entry) applyCatalog(entry);
          }}
        >
          {PROVIDER_CATALOG.map((entry) => (
            <option key={entry.id} value={entry.id}>
              {entry.label}
            </option>
          ))}
        </select>
      </Field>
      <div className="rounded-lg border border-line bg-bg-raised px-2 py-1.5 text-xs text-ink-faint">
        {catalog.capabilities.join(", ")}
      </div>
      <Field label={t("prov.fieldTransport")}>
        <select
          className="w-full rounded-lg border border-line bg-bg-raised px-2 py-1.5 text-sm text-ink"
          value={transport}
          onChange={(e) => setTransport(e.target.value as TransportMode)}
        >
          <option value="direct">{t("prov.transportDirect")}</option>
          <option value="proxy">{t("prov.transportProxy")}</option>
          <option value="gateway">{t("prov.transportGateway")}</option>
        </select>
      </Field>
      {showBaseUrl && (
        <Field label={t("prov.baseUrl")}>
          <input
            className="w-full rounded-lg border border-line bg-bg-raised px-2 py-1.5 text-sm text-ink outline-none focus:border-accent"
            placeholder={t("prov.baseUrlPlaceholder")}
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.target.value)}
          />
        </Field>
      )}
      {transport === "proxy" && (
        <Field label={t("prov.proxyUrl")}>
          <input
            className="w-full rounded-lg border border-line bg-bg-raised px-2 py-1.5 text-sm text-ink outline-none focus:border-accent"
            placeholder={t("prov.proxyUrlPlaceholder")}
            value={proxyUrl}
            onChange={(e) => setProxyUrl(e.target.value)}
          />
        </Field>
      )}
      <Field label={t("prov.defaultModel")}>
        <div className="flex gap-2">
          <input
            className="min-w-0 flex-1 rounded-lg border border-line bg-bg-raised px-2 py-1.5 text-sm text-ink outline-none focus:border-accent"
            placeholder={t("prov.modelPlaceholder")}
            value={model}
            onChange={(e) => setModel(e.target.value)}
          />
          <button
            className="shrink-0 rounded-lg border border-line px-2 py-1.5 text-xs text-ink-soft hover:bg-bg-raised disabled:opacity-50"
            onClick={() => void fetchModels()}
            disabled={fetching || !canFetchModels}
            title={t("prov.fetchTitle")}
          >
            {fetching ? t("prov.fetching") : t("prov.fetch")}
          </button>
        </div>
        {modelChoices.length > 0 && (
          <select
            className="mt-1 w-full rounded-lg border border-line bg-bg-raised px-2 py-1.5 text-sm text-ink"
            value=""
            onChange={(e) => {
              if (e.target.value) setModel(e.target.value);
            }}
          >
            <option value="">{t("prov.modelsSelect", { n: modelChoices.length })}</option>
            {modelChoices.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        )}
      </Field>
      {catalog.capabilities.includes("chat") && (
        <div className="rounded-lg border border-line bg-bg-raised p-2">
          <div className="mb-2 text-xs font-medium text-ink-soft">추론 / 토큰 기본값</div>
          <div className="grid gap-2 sm:grid-cols-3">
            <Field label="Reasoning level">
              <select
                className="w-full rounded-lg border border-line bg-bg-soft px-2 py-1.5 text-sm text-ink"
                value={samplingDefaults.reasoningLevel ?? "auto"}
                onChange={(e) =>
                  setSamplingDefault("reasoningLevel", e.target.value as ReasoningLevel)
                }
              >
                <option value="auto">Auto</option>
                <option value="off">Off / none</option>
                <option value="minimal">Minimal</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="xhigh">XHigh</option>
              </select>
            </Field>
            <Field label="Reasoning budget">
              <input
                type="number"
                min={0}
                step={1}
                className="w-full rounded-lg border border-line bg-bg-soft px-2 py-1.5 text-sm text-ink outline-none focus:border-accent"
                value={samplingDefaults.reasoningBudgetTokens ?? ""}
                onChange={(e) =>
                  setSamplingDefault(
                    "reasoningBudgetTokens",
                    e.target.value === "" ? undefined : Number(e.target.value),
                  )
                }
              />
            </Field>
            <Field label="Tokenizer">
              <select
                className="w-full rounded-lg border border-line bg-bg-soft px-2 py-1.5 text-sm text-ink"
                value={samplingDefaults.tokenizer ?? "auto"}
                onChange={(e) => setSamplingDefault("tokenizer", e.target.value as TokenizerMode)}
              >
                <option value="auto">Auto</option>
                <option value="heuristic">Heuristic</option>
                <option value="o200k_base">o200k_base</option>
                <option value="cl100k_base">cl100k_base</option>
                <option value="p50k_base">p50k_base</option>
                <option value="r50k_base">r50k_base</option>
                <option value="gpt2">gpt2</option>
              </select>
            </Field>
          </div>
          <p className="mt-1 text-xs text-ink-faint">
            프리셋 값이 Auto이거나 비어 있을 때 이 기본값을 적용합니다.
          </p>
        </div>
      )}
      {catalog.requiresApiKey !== false && (
        <Field label={t("prov.apiKey")}>
          <input
            type="password"
            autoComplete="off"
            className="w-full rounded-lg border border-line bg-bg-raised px-2 py-1.5 text-sm text-ink outline-none focus:border-accent"
            placeholder={existing?.keyRef ? t("prov.apiKeyKeepPlaceholder") : t("prov.apiKeyPlaceholder")}
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
          />
        </Field>
      )}

      {err && <p className="text-xs text-red-300">{err}</p>}

      <div className="flex justify-end gap-2 pt-1">
        <button
          className="rounded-lg border border-line px-3 py-1.5 text-sm text-ink-soft hover:bg-bg-raised"
          onClick={onCancel}
          disabled={saving}
        >
          {t("common.cancel")}
        </button>
        <button
          className="rounded-lg bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-soft disabled:opacity-50"
          onClick={save}
          disabled={saving}
        >
          {saving ? t("common.saving") : t("common.save")}
        </button>
      </div>
    </div>
  );
}

function initialCatalogFor(existing: ProviderProfile | undefined): ProviderCatalogEntry {
  if (!existing) return PROVIDER_CATALOG[0]!;
  if (existing.providerId) {
    const entry = getProviderCatalogEntry(existing.providerId);
    if (entry) return entry;
  }
  if (existing.kind === "openai-compatible") {
    return getProviderCatalogEntry("custom-openai-compatible") ?? PROVIDER_CATALOG[0]!;
  }
  return PROVIDER_CATALOG.find((entry) => entry.kind === existing.kind) ?? PROVIDER_CATALOG[0]!;
}

function hasSamplingDefaults(value: ProviderSamplingDefaults): boolean {
  return value.reasoningLevel != null || value.reasoningBudgetTokens != null || value.tokenizer != null;
}

function mergeModelLists(...groups: Array<Iterable<string | undefined>>): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const group of groups) {
    for (const raw of group) {
      const value = raw?.trim();
      if (!value || seen.has(value)) continue;
      seen.add(value);
      out.push(value);
    }
  }
  return out;
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs text-ink-soft">{label}</span>
      {children}
    </label>
  );
}
